import React, { useState, useEffect, useRef } from 'react';
import './leave.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const defaultForm = {
     leave_id: null,
     from_date: '',
     to_date: '',
     leave_type: 'casual',
     reason: '',
     multiple_days: false
};

const formatDateISO = (d) => {
     const dt = new Date(d);
     if (Number.isNaN(dt.getTime())) return '';
     const y = dt.getFullYear();
     const m = String(dt.getMonth() + 1).padStart(2, '0');
     const dd = String(dt.getDate()).padStart(2, '0');
     return `${y}-${m}-${dd}`;
};

const getYesterdayISO = () => {
     const t = new Date();
     t.setDate(t.getDate() - 1);
     return formatDateISO(t);
};
const daysBetweenInclusive = (from, to) => {
     if (!from || !to) return 0;
     const a = new Date(from + 'T00:00:00');
     const b = new Date(to + 'T00:00:00');
     if (isNaN(a) || isNaN(b) || b < a) return 0;
     const diff = Math.round((b - a) / (1000 * 60 * 60 * 24));
     return diff + 1;
};

const LeaveForm = ({ open, onClose, onSubmit, initial = {}, emp_code }) => {
     const [form, setForm] = useState({ ...defaultForm, ...initial });
     const [errors, setErrors] = useState({});
     const [successMsg, setSuccessMsg] = useState('');
     const closeTimer = useRef(null);
     useEffect(() => {
          if (!open) {
               setForm({ ...defaultForm });
               setErrors({});
          }
     }, [open]);
     const [daysCount, setDaysCount] = useState(0);
     const yesterdayISO = getYesterdayISO();
     useEffect(() => {
          return () => {
               if (closeTimer.current) clearTimeout(closeTimer.current);
          };
     }, []);

     useEffect(() => {
          // normalize incoming dates and alternate field names to form shape
          const merged = { ...defaultForm, ...initial };

          // map backend field names -> form fields
          if (!merged.from_date && merged.start_date) merged.from_date = merged.start_date;
          if (!merged.to_date && merged.end_date) merged.to_date = merged.end_date;
          if (!merged.leave_type && merged.type) merged.leave_type = merged.type;
          if (!merged.reason && (merged.remarks || merged.note)) merged.reason = merged.remarks || merged.note;
          if (!merged.leave_id && (merged.id || merged.leave_id)) merged.leave_id = merged.leave_id || merged.id;
          if (!merged.emp_code && (merged.emp_code || merged.applied_by || merged.appliedBy)) merged.emp_code = merged.emp_code || merged.applied_by || merged.appliedBy;
          if (!merged.total_days && merged.days) merged.total_days = Number(merged.days);

          if (merged.from_date) merged.from_date = formatDateISO(merged.from_date);
          if (merged.to_date) merged.to_date = formatDateISO(merged.to_date);

          merged.multiple_days = merged.multiple_days || (merged.from_date && merged.to_date && merged.from_date !== merged.to_date);

          setForm(merged);
          setErrors({});
     }, [initial, open]);

     useEffect(() => {
          // update days count whenever dates/multiple_days change
          if (!form.from_date) {
               setDaysCount(0);
               return;
          }
          const to = form.multiple_days ? (form.to_date || form.from_date) : form.from_date;
          setDaysCount(daysBetweenInclusive(form.from_date, to));
     }, [form.from_date, form.to_date, form.multiple_days]);

     const validate = (f) => {
          const err = {};
          if (!f.from_date) err.from_date = 'Select start date';

          // Only validate to_date if multiple_days is enabled
          if (f.multiple_days && !f.to_date) err.to_date = 'Select end date';

          if (f.from_date && f.to_date && f.from_date > f.to_date) err.range = 'From date must be before or equal to To date';

          // disallow dates strictly less than yesterday
          if (f.from_date && f.from_date < yesterdayISO) err.from_date = 'Cannot apply for dates earlier than yesterday';
          if (f.multiple_days && f.to_date && f.to_date < yesterdayISO) err.to_date = 'Cannot apply for dates earlier than yesterday';

          // ✅ NEW: Make reason mandatory
          if (!f.reason || f.reason.trim() === '') err.reason = 'Reason is required';

          return err;
     };

     const handleChange = (e) => {
          const { name, value, type, checked } = e.target;
          setForm(prev => {
               const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
               // if multiple_days turned off, keep to_date same as from_date
               if (name === 'multiple_days' && !checked) next.to_date = next.from_date || '';
               // if from_date changed and to_date < from_date, adjust to_date to from_date
               if (name === 'from_date' && next.to_date && next.to_date < value) next.to_date = value;
               setErrors({});
               return next;
          });
     };


     const handleSubmit = async (e) => {
          e.preventDefault();
          const validation = validate(form);
          setErrors(validation);
          if (Object.keys(validation).length) return;

          // prepare payload with required shape
          const payload = { ...form };

          // ensure dates are normalized YYYY-MM-DD
          payload.from_date = formatDateISO(payload.from_date);
          payload.to_date = formatDateISO(payload.to_date || payload.from_date);

          // total days (inclusive)
          payload.total_days = daysCount || 1;

          // ensure leave_id is null for new applies (keep original if editing)
          payload.leave_id = payload.leave_id || null;

          // emp_code: prefer form/initial prop, fallback to storage
          payload.emp_code =
               payload.emp_code ||
               emp_code ||
               initial?.emp_code ||
               sessionStorage.getItem('user_code') ||
               sessionStorage.getItem('emp_code') ||
               localStorage.getItem('user_code') ||
               localStorage.getItem('emp_code') ||
               '';

          // trim reason
          if (payload.reason) payload.reason = payload.reason.trim();

          try {
               const res = await onSubmit(payload); // LeaveContext.apply will send to backend

               // handle common response shapes (treat false/success flag)
               if (res && (res.status === false || res.error || res.success === false)) {
                    const msg = res.message || res.error || 'Failed to apply leave';
                    setErrors(prev => ({ ...prev, submit: msg }));
                    toast.error(msg, { autoClose: 3000 });
                    return;
               }

               // success - toast then close modal shortly after
               setErrors({});
               const text = payload.leave_id ? 'Leave updated successfully' : 'Leave applied successfully';
               toast.success(text, { autoClose: 1400 });
               if (closeTimer.current) clearTimeout(closeTimer.current);
               closeTimer.current = setTimeout(() => {
                    onClose();
               }, 1400);
          } catch (err) {
               console.error('Leave apply error:', err);
               const msg = err?.message || 'Network or server error while applying leave';
               setErrors(prev => ({ ...prev, submit: msg }));
               toast.error(msg, { autoClose: 3000 });
          }
     };



     if (!open) return null;

     // Determine min attributes: allow editing old leaves (if editing older than yesterday, don't enforce min)
     const enforceMin = !(form.leave_id && form.from_date && form.from_date < yesterdayISO);

     return (
          <div className="leave-modal-backdrop" onMouseDown={onClose}>
               <div className="leave-modal modal-dialog modal-lg" onMouseDown={e => e.stopPropagation()}>
                    <div className="modal-content p-3">
                         <ToastContainer position="top-right" newestOnTop />
                         <div className="modal-header p-2 pb-0 border-0">
                              <h5 className="modal-title m-0">{form.leave_id ? 'Edit Leave' : 'Apply Leave'}</h5>
                              <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
                         </div>

                         <form className="modal-body pt-2" onSubmit={handleSubmit} noValidate>

                              <div className="row g-2">
                                   <div className={form.multiple_days ? "col-md-6" : "col-md-12"}>
                                        <label className="form-label small">From {!form.multiple_days && "(Leave Date)"}</label>
                                        <input
                                             type="date"
                                             name="from_date"
                                             className={`form-control form-control-sm ${errors.from_date ? 'is-invalid' : ''}`}
                                             value={form.from_date}
                                             onChange={handleChange}
                                             min={enforceMin ? yesterdayISO : undefined}
                                        />
                                        {errors.from_date && <div className="invalid-feedback">{errors.from_date}</div>}
                                   </div>

                                   {/* Only show "To" field when multiple_days is true */}
                                   {form.multiple_days && (
                                        <div className="col-md-6">
                                             <label className="form-label small">To</label>
                                             <input
                                                  type="date"
                                                  name="to_date"
                                                  className={`form-control form-control-sm ${errors.to_date || errors.range ? 'is-invalid' : ''}`}
                                                  value={form.to_date}
                                                  onChange={handleChange}
                                                  min={form.from_date || (enforceMin ? yesterdayISO : undefined)}
                                             />
                                             {(errors.to_date || errors.range) && <div className="invalid-feedback">{errors.to_date || errors.range}</div>}
                                        </div>
                                   )}

                                   <div className="col-md-12 d-flex flex-column">
                                        <div className="mt-2 d-flex align-items-center justify-content-between">
                                             <div>
                                                  <div className="form-check form-switch mt-0">
                                                       <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="multipleDaysSwitch"
                                                            name="multiple_days"
                                                            checked={form.multiple_days}
                                                            onChange={handleChange}
                                                       />
                                                       <label className="form-check-label small" htmlFor="multipleDaysSwitch">Multiple days</label>
                                                  </div>
                                             </div>



                                             <div className="text-end">
                                                  <small className="text-muted">Total days</small>
                                                  <div style={{ fontWeight: 700 }}>{daysCount || '-'}</div>
                                             </div>
                                        </div>
                                   </div>
                                   <div className="col-md-12 mt-2">
                                        <label className="form-label small">Leave Type</label>
                                        <select name="leave_type" className="form-select form-select-sm" value={form.leave_type} onChange={handleChange}>
                                             <option value="">Select Leave Type</option>
                                             <option value="LOP">LOP</option>
                                        </select>
                                   </div>
                              </div>

                              <div className="mt-3">
                                   <label className="form-label small">Reason <span className="text-danger">*</span></label>
                                   <textarea
                                        name="reason"
                                        className={`form-control form-control-sm ${errors.reason ? 'is-invalid' : ''}`}
                                        rows="3"
                                        value={form.reason}
                                        onChange={handleChange}
                                        required
                                        placeholder="Please provide a reason for your leave application"
                                   />
                                   {errors.reason && <div className="invalid-feedback">{errors.reason}</div>}
                              </div>

                              <div className="mt-3 d-flex justify-content-end gap-2">
                                   <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
                                   <button type="submit" className="btn btn-primary btn-sm">{form.leave_id ? 'Update' : `Apply${daysCount ? ` (${daysCount})` : ''}`}</button>
                              </div>
                         </form>
                    </div>
               </div>
          </div>
     );
};

export default LeaveForm;